/**
 * OverviewTab — the hand-off's restructured Briefing Overview (SYR-68, Phase C).
 *
 * Arc: Hero → Q1 (how strong) → Q2 (vs market/benchmark) → Q3 (what's working)
 * → Q4 (what's not working) → Q5 (THE FIX, from the_fix AI) → Q6 (building
 * blocks, from Building Blocks AI — as on /dashboard). Replaces the Phase-1
 * BriefingTab, whose Q-arc was different.
 */

import React, { useCallback, useMemo } from 'react';
import type { IBrandEquityScoreResponse } from '../../../services/nestjs-base-api';
import type { MarketerViewData, ScoreNode } from '../types';
import type { BriefingNarrative, BriefingBenchmarkMetric } from '../useBriefingData';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useNavReturn } from '../nav/NavReturnContext';
import { useElementAccess } from '../../../hooks/useTierAccess';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { Hero } from './Hero';
import { Q1 } from './Q1';
import { Q2 } from './Q2';
import { Q3 } from './Q3';
import { Q2Benchmark } from './Q2Benchmark';
import { TheFixCard } from './TheFixCard';
import { HypothesisCard } from './HypothesisCard';
import { BuildingBlocks } from './BuildingBlocks';

export interface OverviewTabProps {
  data: MarketerViewData;
  narrative: BriefingNarrative | null;
  /** True while the Briefing AI verdict is loading (drives the Hero skeleton). */
  narrativeLoading?: boolean;
  /** True while the per-element AI summaries are loading (Hero meter captions). */
  aiLoading?: boolean;
  response: IBrandEquityScoreResponse | undefined;
  benchmarkMetrics: BriefingBenchmarkMetric[];
  onOpen: (node: ScoreNode) => void;
  sevenName: string;
  /** Briefing label for the Back bar when deep-linking out. */
  briefingLabel: string;
}

export const OverviewTab: React.FunctionComponent<OverviewTabProps> = ({
  data,
  narrative,
  narrativeLoading,
  aiLoading,
  response,
  benchmarkMetrics,
  onOpen,
  sevenName,
  briefingLabel,
}) => {
  const { activeBrand } = useMarketerScope();
  const { navigateWithReturn } = useNavReturn();
  const marketerRoot = useMarketerRoot();

  // Q2–Q5 are rankings/comparisons derived from the element scores. When the
  // tier restricts element access they have nothing to show, so lock the whole
  // section behind a blurred upgrade teaser (Layer 1, always-on). Q1 + Q6 lock
  // per-element instead. Don't lock while access is still loading.
  const { hasUnrestrictedAccess, isLoading: accessLoading } =
    useElementAccess();
  const elementsRestricted = !accessLoading && !hasUnrestrictedAccess;

  // canonical → ScoreNode, so Q6 building-block cards can open the element drawer.
  const nodeByCanonical = useMemo(() => {
    const m = new Map<string, ScoreNode>();
    data.signals.forEach((s) => m.set(s.canonical, s));
    return m;
  }, [data.signals]);

  const goRecommendations = useCallback(() => {
    if (!activeBrand) return;
    // Hidden Recommendations sub-tab of the Briefing; back pill returns here.
    navigateWithReturn(
      `${marketerRoot}/${activeBrand.hash}?tab=recommendations`,
      `${briefingLabel} · Overview`,
    );
  }, [activeBrand, navigateWithReturn, briefingLabel]);

  const goBenchmarks = useCallback(
    (_name: string, level: number) => {
      if (!activeBrand) return;
      // level 1 = latent, 2 = measured — scroll to the matching Benchmarks section.
      const section = level === 1 ? 'latent' : 'measured';
      navigateWithReturn(
        `${marketerRoot}/${activeBrand.hash}/benchmarks?section=${section}`,
        `${briefingLabel} · Overview`,
      );
    },
    [activeBrand, navigateWithReturn, briefingLabel],
  );

  return (
    <main style={{ padding: '0 0 32px' }}>
      {/* SYR-90 — day-0 inferred hypothesis; renders nothing once resolved or absent. */}
      <HypothesisCard />
      <Hero
        data={data}
        narrative={narrative}
        narrativeLoading={narrativeLoading}
        aiLoading={aiLoading}
        onRecommendations={goRecommendations}
        onOpen={onOpen}
        locked={elementsRestricted}
      />
      <Q1
        data={data}
        onOpen={onOpen}
        sevenName={sevenName}
        boostersSummaryLoading={!!narrativeLoading && !narrative?.boosters_summary?.summary}
      />
      <Q2Benchmark
        metrics={benchmarkMetrics}
        overallDiff={data.brand.benchmarkComparison?.diff ?? null}
        onOpen={onOpen}
        nodeByCanonical={nodeByCanonical}
        onElementClick={goBenchmarks}
        locked={elementsRestricted}
      />
      <Q2 data={data} onOpen={onOpen} q="Q3" locked={elementsRestricted} />
      <Q3 data={data} onOpen={onOpen} q="Q4" locked={elementsRestricted} />
      <TheFixCard fix={narrative?.the_fix} locked={elementsRestricted} />
      {response && activeBrand && (
        <BuildingBlocks
          response={response}
          brandId={activeBrand.id}
          onOpen={onOpen}
          nodeByCanonical={nodeByCanonical}
        />
      )}
    </main>
  );
};
